import React, { useCallback } from 'react';
import { HashRouter as Router, Route, Routes, Outlet, useLocation } from 'react-router-dom';
import { GlobalContextProvider } from './utils/GlobalContext';
import useKonamiCode from './utils/useKonamiCode';
import Info from './pages/Info';
import Links from './pages/Links';
import Things from './pages/things/Things';
import ThingDetail from './pages/ThingDetail';
import Notes from './pages/Notes';
import Versions from './pages/Versions';
import DesktopPage from './pages/DesktopPage';
import Subscribe from './pages/Subscribe';
import Contact from './pages/Contact';
import Shop from './pages/Shop';
import ShopItem from './pages/ShopItem';
// LOCAL TEST: hibernated pages temporarily re-enabled — DO NOT PUSH.
import CRTLab from './pages/CRTLab';
import Station from './pages/Station';
// TOS.js and Storage.js are no longer routed — content is now combined
// in Notes.js. Source files preserved for reference.
// Hibernated pages (files preserved in pages/, not routed, dropped from build):
//   CRTLab, Station — WIP visual experiments. Re-enable later
//   by re-adding the import + Route line.
// (ThingFMDev used to sit here too; deleted July 2026.)
// Removed permanently — superseded by dedicated projects:
//   Radio (now ~/Thingdoms Workspace/radio/)
//   Signals + SignalDetail (now ~/Thingdoms Workspace/signals/)
import './Header.css';
import './App.css';
import Header from './Header';
import Rain from './utils/Rain';
import Snow from './utils/Snow';
import useWeather from './utils/useWeather';
import PageTitle from './utils/PageTitle';
import { Navigate, useParams } from 'react-router-dom';
import PROJECTS, { projectUrl } from './utils/projectsData';

// Add ?weather=rain,snow to URL to force effects
function useWeatherDev(realWeather) {
  const params = new URLSearchParams(window.location.search);
  const forced = params.get('weather');
  if (!forced) return realWeather;
  const effects = forced.split(',');
  return {
    rain: effects.includes('rain'),
    snow: effects.includes('snow'),
  };
}

function ArchivalLayout() {
  const realWeather = useWeather();
  const weather = useWeatherDev(realWeather);

  return (
    <div className="App">
      {weather.rain && <Rain />}
      {weather.snow && <Snow />}
      <PageTitle />
      <Header />
      <Outlet />
    </div>
  );
}

/* Old numbered addresses still work. /things/7 lands on the same project it
   always did, at its new name, without leaving the number in the bar. */
function LegacyThingRedirect() {
  const { id } = useParams();
  const project = PROJECTS[parseInt(id, 10) - 1];
  return <Navigate to={project ? projectUrl(project) : '/'} replace />;
}

function KonamiListener() {
  useKonamiCode(useCallback(() => {
    window.location.href = '/desktop';
  }, []));
  return null;
}

export default function App() {
  return (
    <GlobalContextProvider>
      <Router>
        <KonamiListener />
        <Routes>
          <Route path="/desktop" element={<DesktopPage />} />
          {/* LOCAL TEST routes — DO NOT PUSH */}
          <Route path="/crt" element={<CRTLab />} />
          <Route path="/fm" element={<Station />} />
          <Route element={<ArchivalLayout />}>
            <Route path="/" element={<Things />} />
            <Route path="/notes" element={<Notes />} />
            <Route path="/versions" element={<Versions />} />
            <Route path="/info" element={<Info />} />
            <Route path="/links" element={<Links />} />
            <Route path="/mail" element={<Subscribe />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/:slug" element={<ShopItem />} />
            <Route path="/work/:slug" element={<ThingDetail />} />
            <Route path="/things/:id" element={<LegacyThingRedirect />} />
          </Route>
        </Routes>
      </Router>
    </GlobalContextProvider>
  );
}
